import { driverDeviceHistoryQuerySchema, type DriverDevicesView } from '@chukua/common';
import { useCallback, useEffect, useState } from 'react';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { ApiError } from '../../../shared/shared.api';
import { getDriverDevices, revokeDriverDevice, wipeDriverDevice } from '../drivers.api';
import { deviceName } from '../policies/device-event-labels';

export type DriverDevicesState = { status: 'loading' } | { status: 'loaded'; devices: DriverDevicesView } | { status: 'error' };
export type DeviceAction = 'revoke' | 'wipe';

/** The driver's current phone and the page of device history the URL's list query asks for, plus the two things the office can do to the phone. */
export function useDriverDevices(driverId: string) {
  const list = useListQuery(driverDeviceHistoryQuerySchema);
  const { query } = list;
  const [state, setState] = useState<DriverDevicesState>({ status: 'loading' });
  const toast = useToast();

  const load = useCallback(
    () =>
      getDriverDevices(driverId, query)
        .then((devices) => setState({ status: 'loaded', devices }))
        .catch(() => setState({ status: 'error' })),
    [driverId, query],
  );

  useEffect(() => {
    let cancelled = false;
    getDriverDevices(driverId, query)
      .then((devices) => {
        if (!cancelled) setState({ status: 'loaded', devices });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [driverId, query]);

  /** True when the server accepted it, so the reason dialog knows to close. */
  async function act(action: DeviceAction, device: { deviceId: string; model: string | null }, reason: string): Promise<boolean> {
    const name = deviceName(device);
    try {
      await (action === 'revoke' ? revokeDriverDevice : wipeDriverDevice)(driverId, device.deviceId, reason);
      toast.success(action === 'revoke' ? `Device revoked: ${name}` : `Wipe requested for ${name}`);
      await load();
      return true;
    } catch (err) {
      const fallback = action === 'revoke' ? "Couldn't revoke the device" : "Couldn't request the wipe";
      toast.error((err instanceof ApiError && err.status === 409 ? apiErrorMessage(err) : undefined) ?? fallback);
      return false;
    }
  }

  return { state, list, act };
}
